import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import omit from 'lodash.omit';
import isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';
import {
    Box,
    Card,
    Grid,
    IconButton,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    useTheme,
    Tooltip,
    Button,
    Fab,
    CircularProgress
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ForwardIcon from '@mui/icons-material/Forward';

import {
    JinjaCodeEditor,
    BooleanArgument,
    InputFieldArgument,
    DateTimeArgument,
    CategoryArgument,
    MultiSelectArgument,
    ArgumentFieldProps
} from '@tymely/features';
import {
    useArgumentsMetadataQuery,
    useCreateArgumentMetadataMutation,
    useEditArgumentMetadataMutation,
    useRenderTemplateQuery,
    useTemplateFreeVariablesQuery,
    useFetchResponseQuery,
    useEditResponseTemplateMutation,
    useSetAlert
} from '@tymely/services';
import {
    IArgumentMetadata,
    ITemplateVariable,
    IArgument,
    IArgumentBase,
    IResponseTemplate,
    isFlattenCategorical,
    ITemplateError
} from '@tymely/atoms';
import { ArgMetadataEditDialog, defaultArgMetadata } from '@tymely/components';
import { RequiredPick } from '@global/types'
import { TemplateError } from '@tymely/api'

const convertToArgument = (
    argMetadata: IArgumentMetadata,
    variable: ITemplateVariable
): IArgumentBase<typeof argMetadata.dtype, number> => {
    const argument = {
        id: argMetadata.id,
        name: argMetadata.name,
        extractor_cls_name: argMetadata.extractor_name,
        title: argMetadata.title,
        description: argMetadata.description,
        is_edited: true,
        is_unspecified: argMetadata.unspecifiable
            ? variable.is_unspecified
            : false,
        unspecifiable: argMetadata.unspecifiable,
        neitherable: argMetadata.options?.neitherable ?? true,
        dtype: argMetadata.dtype,
        arg_type: argMetadata.arg_type,
        order: 1,
        value: variable.value,
        categories: argMetadata.options?.categories
    }

    return argument;
}
export const ArgEditor = memo(
    (props: {
        argument: IArgument;
        onChange?: ArgumentFieldProps<IArgument>['onChange'];
    }) => {
        switch (props.argument.dtype) {
            case 'BOOL':
                return (
                    <BooleanArgument
                        {...props}
                        withLabel
                        argument={props.argument}
                    />
                );
            case 'IMG':
            case 'URL':
            case 'VIDEO_URL':
            case 'LIST':
            case 'INT':
            case 'STRING':
                return (
                    <InputFieldArgument
                        {...props}
                        withLabel
                        argument={props.argument}
                    />
                );
            case 'DATETIME':
                return (
                    <DateTimeArgument
                        {...props}
                        withLabel
                        argument={props.argument}
                    />
                );
            case 'CATEGORICAL':
            case 'MULTI_CATEGORICAL':
                if (isFlattenCategorical(props.argument)) {
                    return (
                        <CategoryArgument
                            {...props}
                            withLabel
                            argument={props.argument}
                        />
                    );
                }
                return (
                    <MultiSelectArgument
                        {...props}
                        withLabel
                        argument={props.argument}
                    />
                );
        }
    }
);

const Variable = memo(
    (props: {
        name: string;
        argMetadata?: IArgumentMetadata;
        value: ITemplateVariable;
        onChange: (varName: string, value: ITemplateVariable) => void;
    }) => {
        const [editOpen, setEditOpen] = useState(false);
        const editArgMetadata = useEditArgumentMetadataMutation();
        const createArgMetadata = useCreateArgumentMetadataMutation();
        const [argMetadata, setArgMetadata] = useState(
            props.argMetadata ?? {
                ...omit(defaultArgMetadata, 'id'),
                name: props.name,
                id: 0
            }
        );

        const argument = useMemo(
            () => convertToArgument(argMetadata, props.value),
            [argMetadata, props.value]
        );

        const onSubmitArgMetadata = useCallback(
            (argMeta: IArgumentMetadata) => {
                const onSuccess = ({id: argMetaId}: IArgumentMetadata) => {
                    if (argMetadata.dtype !== argMeta.dtype) {
                        props.onChange(argMeta.name, {
                            ...props.value,
                            is_unspecified: false,
                            is_neither: false,
                            value: ''
                        });
                    }
                    if (
                        !isEqual(
                            argMeta.options?.categories,
                            argMetadata.options?.categories
                        )
                    ) {
                        props.onChange(argMeta.name, {
                            ...props.value,
                            value: ''
                        });
                    }
                    if (
                        argMetadata.options?.neitherable !==
                        argMeta.options?.neitherable
                    ) {
                        props.onChange(argMeta.name, {
                            ...props.value,
                            value: '',
                            is_neither: false
                        });
                    }
                    if (argMetadata.unspecifiable !== argMeta.unspecifiable) {
                        props.onChange(argMeta.name, {
                            ...props.value,
                            is_unspecified: false
                        });
                    }
                    setArgMetadata({...argMeta, id: argMetaId});
                    setEditOpen(false);
                };

                if (argMetadata.id) {
                    return editArgMetadata.mutateAsync(argMeta).then(onSuccess);
                } else {
                    return createArgMetadata
                        .mutateAsync({metadata: argMeta})
                        .then(onSuccess);
                }
            },
            [argMetadata, editArgMetadata, createArgMetadata, props.onChange]
        );

        const onChange = useCallback(
            ([argument]: IArgument[]) => {
                props.onChange(argument.name, {
                    ...props.value,
                    value: argument.value,
                    is_unspecified: false,
                    is_neither: argument.value === null
                });
            },
            [props.value, props.onChange]
        );

        const onUnspecify = useCallback(() => {
            props.onChange(argument.name, {
                ...props.value,
                value: null,
                is_unspecified: true,
                is_neither: false
            });
        }, [argument, props.value, props.onChange]);

        return (
            <>
                <Box display="flex" justifyContent="center" mb={2}>
                    <FormControl size="small" sx={{flex: 1}}>
                        <InputLabel>{argument.name}</InputLabel>
                        <ArgEditor
                            argument={argument as IArgument}
                            onChange={onChange}
                        />
                    </FormControl>
                    <Tooltip title="Unspecified" enterDelay={0.5}>
                        <Box display="inline-flex">
                            <IconButton
                                aria-label="unspecified"
                                disabled={
                                    argument.is_unspecified ||
                                    !argument.unspecifiable
                                }
                                onClick={onUnspecify}
                            >
                                {argument.is_unspecified ? (
                                    <RadioButtonCheckedIcon fontSize="small"/>
                                ) : (
                                    <RadioButtonUncheckedIcon fontSize="small"/>
                                )}
                            </IconButton>
                        </Box>
                    </Tooltip>
                    <IconButton
                        disabled={editArgMetadata.isLoading}
                        onClick={() => setEditOpen(true)}
                    >
                        {props.argMetadata ? (
                            <EditIcon fontSize="small"/>
                        ) : (
                            <AddIcon fontSize="small"/>
                        )}
                    </IconButton>
                </Box>
                {editOpen && (
                    <ArgMetadataEditDialog
                        open
                        title="Edit argument metadata"
                        argMetadata={argMetadata}
                        onSubmit={onSubmitArgMetadata}
                        onClose={() => setEditOpen(false)}
                    />
                )}
            </>
        );
    }
);

const Variables = memo(
    (props: {
        template: string;
        setVariables: (variables: Record<string, ITemplateVariable>) => void;
    }) => {
        const [argMetadata, setArgMetadata] = useState<Record<string, IArgumentMetadata>>({});
        const argumentsMetadataQuery = useArgumentsMetadataQuery();

        const [variables, setVariables] = useState<
            Record<string, ITemplateVariable>
        >({});
        const freeVariablesQuery = useTemplateFreeVariablesQuery(
            props.template
        );

        useEffect(() => {
            if (!argumentsMetadataQuery.data || !freeVariablesQuery.data)
                return;

            const argMetadata = argumentsMetadataQuery.data.reduce<
                Record<string, IArgumentMetadata>
            >((acc, item) => ({...acc, [item.name]: item}), {});
            const vars = freeVariablesQuery.data.reduce<
                Record<string, ITemplateVariable>
            >(
                (acc, varName) => ({
                    ...acc,
                    [varName]: {
                        value:
                            variables[varName]?.value !== undefined
                                ? variables[varName]?.value
                                : '',
                        dtype: argMetadata[varName]?.dtype ?? 'STRING',
                        is_list: argMetadata[varName]?.is_list ?? false,
                        is_neither: variables[varName]?.is_neither ?? false,
                        is_unspecified:
                            variables[varName]?.is_unspecified ?? false
                    }
                }),
                {}
            );
            setArgMetadata(argMetadata);
            setVariables(vars);
            props.setVariables(vars);
        }, [
            variables,
            argumentsMetadataQuery.data,
            freeVariablesQuery.data,
            props.setVariables
        ]);

        const onVariableChange = useCallback(
            (varName: string, variable: ITemplateVariable) => {
                const newVars = {
                    ...variables,
                    [varName]: {...variables[varName], ...variable}
                };
                props.setVariables(newVars);
                setVariables(newVars);
            },
            [variables, props.setVariables]
        );

        if (Object.keys(variables).length === 0) {
            if (freeVariablesQuery.isError || argumentsMetadataQuery.isError) {
                return <Typography color="error">Error</Typography>;
            }

            if (
                freeVariablesQuery.isLoading ||
                argumentsMetadataQuery.isLoading
            ) {
                return <Typography>Loading...</Typography>;
            }

            return (
                <Typography color="gray">
                    No free variables detected in the template
                </Typography>
            );
        }

        return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                {Object.keys(variables)
                    .sort((v1, v2) => v1.localeCompare(v2))
                    .map((variableName) => (
                        <Variable
                            key={variableName}
                            name={variableName}
                            value={variables[variableName]}
                            argMetadata={argMetadata[variableName]}
                            onChange={onVariableChange}
                        />
                    ))}
            </LocalizationProvider>
        );
    },
    (prevProps, nextProps) => prevProps.template === nextProps.template
);

export const TemplateEditor = (props: { showBackButton?: boolean, onBack?: () => void }) => {
    const theme = useTheme();
    const [templateErrors, setTemplateErrors] = useState<ITemplateError[]>([]);
    const showAlert = useSetAlert();
    const [template, setTemplate] = useState<IResponseTemplate | undefined>(); //'{%- macro PaymentMethodVar(payment_method) -%}\n    {%- if payment_method == \'CREDIT_CARD\' -%}\n        credit card\n    {%- elif payment_method == \'GIFT_CARD\' -%}\n        gift card\n    {%- elif payment_method == \'ONLINE_WALLET\' -%}\n        account\n    {%- endif -%}\n{%- endmacro -%}\n\n{%- set RefundPaymentMethodVar -%}\n{%- if (refund_transaction_account_types | length) == 1 -%}\n    {{ PaymentMethodVar(refund_transaction_account_types[0]) }}\n{%- else -%}\n    corresponding payment methods\n{%- endif -%}\n{%- endset -%}\n\n{%- set QualityIssueMentionedVar -%}\n{%- if quality_issue_mentioned -%}\n     We do take quality control seriously and we would like to ensure that you are taken care of appropriately and efficiently. Did you happen to take any images of the {{ ItemType }} so I can take a look and report it to my factory? If you\'re able to send them to me I\'d be happy to help.\n{%- else -%}\n{%- endif -%}\n{%- endset -%}\n\n{%- set calc_within | int -%}\n{%- if \'Sezzle Payments\' in refund_payment_providers -%}\n    10\n{%- else -%}\n    3\n{%- endif -%}\n{%- endset -%}\n\n{%- set refund_clear_date -%}\n{% if time_of_refund %}\nuntil {{ (time_of_refund | workdays( calc_within )).strftime(\'%m/%d/%Y\') }}\n{%- elif \'Sezzle Payments\' in refund_payment_providers -%}\n    within the next 10 business days\n{%- else -%}\n    within the next 2 to 3 business days\n{%- endif -%}\n{%- endset -%}\n\n{%- set Refund_was_processed_date -%}\n{% if time_of_refund %}\non {{ (time_of_refund).strftime(\'%m/%d/%Y\') }}\n{%- else -%}\n{% endif %}\n{%- endset -%}\n\n\nHey {{ CustomerFirstName }},\n\nThanks for getting in touch with Thursday Boot Company! I\'d be happy to check on your refund status.\n\nI followed up on your return and saw that a refund has been processed to your {{ RefundPaymentMethodVar }} {{ Refund_was_processed_date }}. \n\nYou should receive an email confirmation for this. If you don\'t receive the refund {{ refund_clear_date }}, please let me know and I\'ll gladly follow up on this for you.\n{{ QualityIssueMentionedVar }}\nHope this was helpful. Should you need anything else, I\'m standing by.\n\nSincerely,\nMindee');
    const [variables, setVariables] = useState<Record<string, ITemplateVariable>>({});
    const templateText = template?.response_txt || '';

    const pathParams = useParams<{ templateId: string }>();
    const {data: loadedTemplate} = useFetchResponseQuery(
        Number(pathParams.templateId),
        {initialData: {id: 0, response_txt: ''}}
    );
    const {mutate: updateTemplate, isLoading: isUpdating} = useEditResponseTemplateMutation({
        onError: (err) => {
            if (err instanceof TemplateError) {
                setTemplateErrors(err.detail)
            }
        }
    });
    const hasChanges = useMemo(
        () => loadedTemplate?.response_txt !== templateText,
        [loadedTemplate, templateText]
    );

    useEffect(() => {
        setTemplate(loadedTemplate);
    }, [loadedTemplate]);

    const {data: renderedTemplate} = useRenderTemplateQuery(
        templateText,
        variables,
        {
            onSuccess: () => {
                setTemplateErrors([]);
            },
            onError: (err) => {
                if (err instanceof TemplateError) {
                    setTemplateErrors(err.detail);
                }
            },
            retry: false
        }
    );

    const onCodeChange = useCallback(
        debounce((text: string) => {
            setTemplate((template) =>
                template ? {...template, response_txt: text} : template
            );
        }, 300),
        [setTemplate]
    );

    const {data} = useArgumentsMetadataQuery();
    const metaArgs = useMemo(() => new Set<string>(data?.map((item) => item.name)), [data]);

    const onSave = useCallback(() => {
        if (!template?.id) return;
        const newArg = Object.keys(variables).find((key) => !metaArgs.has(key));
        if (newArg) {
            showAlert(`Please create "${newArg}" argument first`, 'error');
            return;
        }
        updateTemplate(template);
    }, [template, variables, metaArgs, showAlert, updateTemplate]);

    const onReset = useCallback(() => {
        setTemplate(loadedTemplate);
    }, [loadedTemplate]);

    return (
        <Grid container width="100%" maxWidth="2200px" height={1}>
            <Grid item xs={3} height={1}>
                <Card
                    sx={{
                        mr: 2,
                        height: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                    }}
                >
                    <Box p={2}>
                        <Typography variant="h4" mb={1}>
                            Variables
                        </Typography>
                        <Variables
                            template={templateText}
                            setVariables={setVariables}
                        />
                    </Box>
                    {props.showBackButton && (
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<ForwardIcon sx={{transform: 'rotate(-180deg)'}}/>}
                            sx={{borderRadius: 0}}
                            disabled={hasChanges}
                            onClick={props.onBack}
                        >
                            Back
                        </Button>
                    )}
                </Card>
            </Grid>
            <Grid
                item
                xs={9}
                height={1}
                display="flex"
                flexDirection="column"
            >
                <Card
                    sx={{
                        height: 0.6,
                        marginBottom: 2,
                        border: '2px solid transparent',
                        overflowY: 'auto',
                        position: 'relative',
                        ...(templateErrors.length
                            ? {borderColor: theme.palette.error.light}
                            : {})
                    }}
                >
                    <JinjaCodeEditor
                        code={templateText}
                        height="100%"
                        onChange={onCodeChange}
                        errors={templateErrors}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            display: 'flex',
                            flexDirection: 'column',
                            right: theme.spacing(4),
                            bottom: theme.spacing(2)
                        }}
                    >
                        <Fab
                            title="Reset"
                            size="medium"
                            onClick={onReset}
                            sx={{
                                '&:hover': {
                                    color: theme.palette.warning.contrastText,
                                    backgroundColor: theme.palette.warning.main
                                }
                            }}
                        >
                            <RefreshIcon/>
                        </Fab>
                        <Fab
                            title="Apply"
                            size="medium"
                            disabled={template === undefined || !!templateErrors.length || !hasChanges}
                            sx={{
                                '&:hover': {
                                    color: theme.palette.success.contrastText,
                                    backgroundColor: theme.palette.success.main
                                },
                                marginTop: 2
                            }}
                            onClick={onSave}
                        >
                            {isUpdating ? (
                                <CircularProgress size={20}/>
                            ) : (
                                <CheckIcon/>
                            )}
                        </Fab>
                    </Box>
                </Card>
                <Card sx={{flexGrow: 1}}>
                    <TextField
                        fullWidth
                        multiline
                        variant="standard"
                        value={renderedTemplate}
                        InputProps={{
                            disableUnderline: true,
                            readOnly: true
                        }}
                        sx={{
                            p: 2,
                            height: '100%',
                            overflowY: 'scroll'
                        }}
                    />
                </Card>
            </Grid>
        </Grid>
    );
};
